import { ShimmerText } from '@/components/feedback/shimmer-text';
import { useAnalytics } from '@/services/analytics';
import { hydraUiApi, useHydraUIAPIMutation } from '@/services/api';
import type { CopilotRecordFeedbackRequest } from '@customerio/hydra-ui-api';
import { useAuth } from '@/services/auth';
import { AccountSetting, useAccountSetting } from '@/services/auth/account-settings';
import { getCurrentRegion, Region } from '@/utils/environment';
import { copyToClipboard } from '@/hooks/user-actions/use-copy-to-clipboard';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import type { CopilotSession, CopilotStatefulMessage } from '@customerio/hydra-copilot';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Banner,
  BannerDescription,
  Box,
  Button,
  ButtonGroup,
  CodeBlock,
  Flex,
  Form,
  FormLayout,
  Icon,
  InlineStack,
  Link,
  Panel,
  PanelSection,
  Paragraph,
  PlainButton,
  Popover,
  PopoverContent,
  Stack,
  Text,
  TextArea,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { m } from 'motion/react';
import plur from 'plur';
import type { FormEvent } from 'react';
import React, { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Attachments } from '../attachments';
import { Markdown } from '../primitives/markdown';
import { MessageAction, MessageActionsContainer, MessageContainer } from '../primitives/message-container';
import { References, type Reference } from '../references';
import { AnimateMessage } from './animate-message';
import { ChatUserAvatar } from './chat-user-avatar';
import { ToolCalls } from './tool-calls';
import * as chatStyles from '../../chat.css';
import { fillScope } from '@customerio/pluma-components/css/utils';

type ErrorMessageProps = { error: string; errorDetails?: string };

function isRateLimitError(error: string, errorDetails?: string): boolean {
  const haystack = `${error} ${errorDetails ?? ''}`.toLowerCase();
  return haystack.includes('429') || haystack.includes('rate limit');
}

function RateLimitHintBanner() {
  const useGlobalEndpoint = useAccountSetting(AccountSetting.UseGlobalAiEndpoint);
  const isEu = getCurrentRegion() === Region.EU;
  if (!isEu || useGlobalEndpoint) {
    return null;
  }
  return (
    <Banner
      variant="caution"
      title="EU endpoint has lower capacity"
      description={
        <>
          You're using the EU endpoint, which has lower capacity and is more likely to hit rate limits. If you're
          comfortable with your data being processed in other regions, you can enable the global endpoint for higher
          limits, more models, and better performance in{' '}
          <Link href="/settings/ai" isUnderlined>
            AI &amp; MCP settings
          </Link>
          .
        </>
      }
    />
  );
}

function ErrorMessage({ error, errorDetails }: ErrorMessageProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Stack gap="100">
      <Banner variant="critical" title="Error">
        <BannerDescription>{error}</BannerDescription>
        {!isEmptyString(errorDetails) ? (
          <Stack gap="075">
            <PlainButton
              display="flex"
              alignItems="center"
              gap="025"
              onClick={() => setIsOpen((previous) => !previous)}
              aria-expanded={isOpen}
            >
              <Text size="xs" color="secondary">
                {isOpen ? 'Hide details' : 'Show details'}
              </Text>
              <m.div
                animate={{ rotate: isOpen ? 90 : 0 }}
                transition={{ duration: 0.15 }}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Icon name="chevron-right" size="sm" color="secondary" />
              </m.div>
            </PlainButton>
            {isOpen ? <CodeBlock code={errorDetails} language="text" /> : null}
          </Stack>
        ) : null}
      </Banner>
      {isRateLimitError(error, errorDetails) ? <RateLimitHintBanner /> : null}
    </Stack>
  );
}

type CompactionMessageProps = { status: 'compacting' | 'complete'; summary?: string };

function CompactionMessage({ status, summary }: CompactionMessageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const hasSummary = summary != null && summary !== '';
  const isExpandable = status === 'complete' && hasSummary;

  useEffect(() => {
    if (status !== 'compacting') {
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      setProgress(100);
      return;
    }

    // Animate progress with diminishing increments (never reaches 100 until complete)
    const interval = setInterval(() => {
      setProgress((previous) => {
        const remaining = 90 - previous;
        return previous + remaining * 0.015;
      });
    }, 200);

    return () => clearInterval(interval);
  }, [status]);

  return (
    <AnimateMessage>
      <Box display="flex" flexDirection="column" gap="050" py="100">
        {status === 'compacting' ? (
          <>
            <Text size="sm" color="secondary" fontStyle="italic">
              Compacting our conversation so we can keep chatting…
            </Text>
            <Box display="flex" alignItems="center" gap="100">
              <Box
                flexGrow="1"
                style={{
                  height: '4px',
                  borderRadius: themeVars.radius['full'],
                  backgroundColor: fillScope('neutral', 'subtle'),
                  overflow: 'hidden',
                }}
              >
                <Box
                  h="full"
                  style={{
                    width: `${Math.round(progress)}%`,
                    borderRadius: themeVars.radius['full'],
                    backgroundColor: themeVars.color['surface-track'],
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
              <Text size="xs" color="secondary">
                {Math.round(progress)}%
              </Text>
            </Box>
          </>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap="050"
            cursor={isExpandable ? 'pointer' : undefined}
            onClick={isExpandable ? () => setIsOpen((previous) => !previous) : undefined}
          >
            {isExpandable ? (
              isOpen ? (
                <Icon name="chevron-down" size="sm" />
              ) : (
                <Icon name="chevron-right" size="sm" />
              )
            ) : null}
            <Icon color="success" name="check-circle" size="sm" />
            <Text size="xs" color="secondary">
              Conversation compacted
            </Text>
          </Box>
        )}
        {isOpen && hasSummary ? (
          <Box px="100" pt="050">
            <Markdown>{summary}</Markdown>
          </Box>
        ) : null}
      </Box>
    </AnimateMessage>
  );
}

type ThoughtMessageProps = {
  message: CopilotStatefulMessage & { type: 'text'; thought?: boolean };
  isStreaming?: boolean;
};

function ThoughtMessage({ message, isStreaming }: ThoughtMessageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wasStreamingRef = useRef(false);

  const durationSeconds = 'duration' in message && typeof message.duration === 'number' ? message.duration : null;

  const formattedDuration = (() => {
    if (durationSeconds == null) return null;

    if (durationSeconds < 60) {
      return `${durationSeconds} ${plur('second', durationSeconds)}`;
    } else {
      const minutes = Math.floor(durationSeconds / 60);
      const seconds = durationSeconds % 60;
      if (seconds === 0) {
        return `${minutes} ${plur('minute', minutes)}`;
      }
      return `${minutes} ${plur('minute', minutes)} ${seconds} ${plur('second', seconds)}`;
    }
  })();

  // Auto-open when streaming starts, auto-close when streaming ends
  useEffect(() => {
    if (isStreaming === true) {
      wasStreamingRef.current = true;
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      setIsOpen(true);
    } else if (wasStreamingRef.current) {
      wasStreamingRef.current = false;
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      setIsOpen(false);
    }
  }, [isStreaming]);

  return (
    <AnimateMessage>
      <Box display="flex" flexDirection="column">
        <Box
          display="flex"
          alignItems="center"
          gap="075"
          py="075"
          cursor="pointer"
          onClick={() => setIsOpen((previous) => !previous)}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsOpen((previous) => !previous);
            }
          }}
        >
          <Text as={formattedDuration == null ? ShimmerText : 'span'} color="secondary" size="sm">
            {formattedDuration != null ? `Thought for ${formattedDuration}` : 'Thinking\u2026'}
          </Text>
          <m.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon name="chevron-down" size="sm" color="secondary" />
          </m.div>
        </Box>
        <m.div
          initial={false}
          animate={{
            height: isOpen ? 'auto' : 0,
            opacity: isOpen ? 1 : 0,
          }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          <Box py="075">
            <Markdown isAnimating={isStreaming} color="secondary" size="sm">
              {message.text}
            </Markdown>
          </Box>
        </m.div>
      </Box>
    </AnimateMessage>
  );
}

type MessageProps = {
  message: CopilotStatefulMessage;
  session: CopilotSession;
  isLastMessage?: boolean;
  isStreaming?: boolean;
  /** True while recovery is still polling — suppresses the incomplete indicator. */
  isRecovering?: boolean;
  /** True when the user deliberately stopped — suppresses the incomplete indicator (a stop isn't an interruption). */
  isStopped?: boolean;
  /** Known skill slugs for highlighting /skill-name tokens in user messages */
  skillSlugs?: Set<string>;
};

export function getMessageKey(message: CopilotStatefulMessage, index: number) {
  // We do not get stable backend IDs for messages, so this uses the row's
  // append-order identity while avoiding fields like timestamp that change as
  // streaming and compaction messages settle.
  const messageType = message.role === 'model' ? message.type : 'user';
  return `${message.role}:${messageType}:${index}`;
}

function MessageComponent({
  message,
  session,
  isLastMessage = false,
  isStreaming = false,
  isRecovering = false,
  isStopped = false,
  skillSlugs,
}: MessageProps) {
  const auth = useAuth();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();

  const [feedbackReasonOpen, setFeedbackReasonOpen] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [actionTaken, setActionTaken] = useState(false);

  const submitFeedbackMutation = useHydraUIAPIMutation(
    (variables: { sessionId: number; logId: string; body: CopilotRecordFeedbackRequest }) =>
      hydraUiApi.copilot.logsFeedback({
        params: {
          path: {
            account_id: String(auth.account.id),
            session_id: String(variables.sessionId),
            log_id: variables.logId,
          },
        },
        body: variables.body,
      }),
    {
      onSuccess: (_, variables) => {
        snackbars.create({
          message: 'Thanks for your feedback.',
        });

        if (message.role !== 'model' || message.type !== 'text' || message.meta?.id == null) {
          return;
        }

        session.setMessageMeta(message.meta.id, {
          ...message.meta,
          thumbs_up: variables.body?.thumbs_up,
          thumbs_down: variables.body?.thumbs_down,
        });

        setFeedbackReasonOpen(false);
        setFeedbackReason('');
      },
      onError: (error) => {
        sentryCaptureException(error);
        snackbars.create({
          message: 'Your feedback was not submitted. Try again.',
          variant: 'critical',
        });
      },
    },
  );

  const submitThumbsDown = useCallback(
    (reason?: string) => {
      if (message.role !== 'model' || message.type !== 'text' || message.meta?.id == null) {
        return;
      }

      submitFeedbackMutation.mutate({
        sessionId: Number(session.id),
        logId: message.meta.id,
        body: {
          thumbs_down: true,
          ...(!isEmptyString(reason) ? { feedback_reason: reason } : {}),
        },
      });
      track('aiAssistant', 'thumbsDown', {
        'Feedback Text': reason ?? '',
        'Has Feedback': !isEmptyString(reason),
      });
    },
    [auth.account.id, message, session.id, submitFeedbackMutation, track],
  );

  const fileChips = useMemo(() => {
    if (message.role !== 'user') return [];

    return message.attachments.map((attachment, index) => ({
      id: `readonly-${index}`,
      name: attachment.display_name ?? 'File',
      mimeType: attachment.mime_type ?? 'application/octet-stream',
      source: attachment.content,
    }));
  }, [message]);

  if (message.role === 'user') {
    const textContent = !isEmptyString(message.text) ? (
      <Markdown skillSlugs={skillSlugs}>{message.text}</Markdown>
    ) : (
      <Paragraph size="sm" color="secondary" fontStyle="italic">
        No message
      </Paragraph>
    );

    return (
      <Box display="flex" flexDirection="column" gap="100">
        {fileChips.length > 0 ? (
          // Inset by the avatar column (sm avatar 24px + gap 100) so chips share the bubble's right edge.
          <Box display="flex" justifyContent="flex-end" pr="400">
            <Attachments files={fileChips} session={session} variant="grid" />
          </Box>
        ) : null}
        <Flex justifyContent="flex-end" alignItems="flex-start" gap="100">
          {/* Fills the row so the bubble's max-width: 80% resolves against the row rather than
              its own shrink-to-fit width, which would wrap short messages mid-word. */}
          <MessageContainer variant="user" flexGrow="1" minW="0">
            <Box display="flex" flexDirection="column" gap="100">
              {textContent}
            </Box>
          </MessageContainer>
          <ChatUserAvatar />
        </Flex>
      </Box>
    );
  } else if (message.role === 'model' && message.type === 'text') {
    const sources: Reference[] = 'meta' in message && message.meta?.sources != null ? message.meta.sources : [];
    const otherSources = sources.filter((source) => source?.type !== 'business_profile');

    const hasMetaId = 'meta' in message && message.meta?.id != null;
    const hasThumbsUp = 'meta' in message && message.meta?.thumbs_up === true;
    const hasThumbsDown = 'meta' in message && message.meta?.thumbs_down === true;
    const isThought = 'thought' in message && message.thought === true;
    const showReferences = otherSources.length > 0;
    const pausedAtTurnLimit = 'meta' in message && message.meta?.paused_at_turn_limit === true;
    const pauseRecap = 'meta' in message ? message.meta?.pause_recap : undefined;

    const finishReason = 'meta' in message ? message.meta?.finish_reason : undefined;
    // Backend closes every complete exchange with a meta, so a settled last bubble lacking one
    // was cut off. Last bubble only: intermediate pre-tool-call text legitimately has no meta yet.
    // Non-empty text avoids a lone indicator over a blank bubble. !isStopped: a deliberate stop isn't an interruption.
    const isInterrupted =
      isLastMessage && !isStreaming && !isRecovering && !isStopped && !hasMetaId && !isEmptyString(message.text);
    let incompleteLabel: string | null = null;
    if (finishReason === 'max_tokens') {
      incompleteLabel = 'This response was cut off at its length limit.';
    } else if (finishReason === 'filtered') {
      incompleteLabel = 'This response was stopped early.';
    } else if (isInterrupted) {
      incompleteLabel = 'This response was interrupted and is incomplete.';
    }

    if (isThought) {
      return <ThoughtMessage message={message} isStreaming={isStreaming} />;
    }

    return (
      <AnimateMessage>
        <MessageContainer variant="model">
          {pausedAtTurnLimit && isLastMessage && pauseRecap != null && pauseRecap !== '' ? (
            <Panel>
              <PanelSection>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap="150">
                  <InlineStack alignBlock="start" gap="100">
                    <Icon name="sparkle" color="secondary" size="sm" />
                    <Box display="flex" flexDirection="column" gap="025">
                      <Text size="sm" weight="medium">
                        Agent paused to check in before continuing a long task
                      </Text>
                      <Text size="xs" color="secondary" className={chatStyles.pauseRecapClamp}>
                        {pauseRecap}
                      </Text>
                    </Box>
                  </InlineStack>
                  <Button
                    size="sm"
                    variant="primary"
                    isDisabled={actionTaken}
                    onClick={() => {
                      setActionTaken(true);
                      void session.sendMessage('Continue').catch(() => setActionTaken(false));
                    }}
                  >
                    Continue
                  </Button>
                </Box>
              </PanelSection>
            </Panel>
          ) : (
            <Markdown isAnimating={isStreaming}>{message.text}</Markdown>
          )}
          {incompleteLabel != null ? (
            <Box pt="050">
              <InlineStack gap="050" alignBlock="center">
                <Icon name="caution" size="sm" color="secondary" />
                <Text size="xs" color="secondary">
                  {incompleteLabel}
                </Text>
              </InlineStack>
            </Box>
          ) : null}
          {hasMetaId || showReferences ? (
            <Box>
              <MessageActionsContainer isVisible={isLastMessage} alignBlock="center" alignInline="space-between">
                {hasMetaId ? (
                  <InlineStack gap="050" alignBlock="center">
                    <MessageAction
                      tooltip="Copy to clipboard"
                      onClick={async () => {
                        const success = await copyToClipboard(message.text);
                        if (success) {
                          snackbars.create({
                            message: 'Copied to clipboard',
                          });
                          track('aiAssistant', 'messageCopied', {});
                        }
                      }}
                      aria-label="Copy to clipboard"
                    >
                      <Icon name="copy-to-clipboard" size="sm" />
                    </MessageAction>
                    {!hasThumbsDown ? (
                      <MessageAction
                        tooltip="Helpful"
                        variant={hasThumbsUp ? 'active' : 'default'}
                        onClick={
                          hasThumbsUp
                            ? undefined
                            : () => {
                                submitFeedbackMutation.mutate({
                                  sessionId: Number(session.id),
                                  logId: message.meta.id,
                                  body: {
                                    thumbs_up: true,
                                  },
                                });
                                track('aiAssistant', 'thumbsUp', {});
                              }
                        }
                        aria-label="Thumbs up"
                      >
                        <Icon name="thumbs-up" size="sm" />
                      </MessageAction>
                    ) : null}
                    {!hasThumbsUp ? (
                      <Popover
                        placement="bottom-start"
                        withArrow={false}
                        isOpen={feedbackReasonOpen}
                        shouldTriggerOnClick={!hasThumbsDown}
                        onOpenChange={(open) => {
                          if (!open && !hasThumbsDown && !submitFeedbackMutation.isPending) {
                            submitThumbsDown(feedbackReason);
                          }
                          setFeedbackReasonOpen(open);
                          if (!open) {
                            setFeedbackReason('');
                          }
                        }}
                      >
                        {(context) => (
                          <MessageAction
                            ref={context.reference.setReference}
                            {...context.reference.getReferenceProps()}
                            tooltip={feedbackReasonOpen ? undefined : 'Not helpful'}
                            variant={hasThumbsDown ? 'active' : 'default'}
                            aria-label="Thumbs down"
                          >
                            <Icon name="thumbs-down" size="sm" />
                          </MessageAction>
                        )}
                        <PopoverContent>
                          <Form
                            onSubmit={(event: FormEvent) => {
                              event.preventDefault();
                              submitThumbsDown(feedbackReason);
                            }}
                          >
                            <FormLayout>
                              <Text size="sm" weight="semibold">
                                What went wrong?
                              </Text>
                              <TextArea
                                ariaLabel="Feedback reason"
                                placeholder="Tell us what could be improved"
                                value={feedbackReason}
                                onInput={(event) => {
                                  setFeedbackReason(event.target.value);
                                }}
                                rows={3}
                                style={{ minWidth: '260px' }}
                              />
                              <Flex justifyContent="end">
                                <ButtonGroup groupVariant="spaced">
                                  <Button
                                    variant="tertiary"
                                    size="sm"
                                    type="button"
                                    isDisabled={submitFeedbackMutation.isPending}
                                    onClick={() => {
                                      submitThumbsDown();
                                    }}
                                  >
                                    Skip
                                  </Button>
                                  <Button
                                    variant="primary"
                                    type="submit"
                                    size="sm"
                                    isLoading={submitFeedbackMutation.isPending}
                                  >
                                    Send feedback
                                  </Button>
                                </ButtonGroup>
                              </Flex>
                            </FormLayout>
                          </Form>
                        </PopoverContent>
                      </Popover>
                    ) : null}
                  </InlineStack>
                ) : (
                  <div />
                )}
                {showReferences ? (
                  <InlineStack gap="100" alignBlock="center">
                    <References references={otherSources} />
                  </InlineStack>
                ) : null}
              </MessageActionsContainer>
            </Box>
          ) : null}
        </MessageContainer>
      </AnimateMessage>
    );
  } else if (message.role === 'model' && message.type === 'tool_call') {
    return <ToolCalls message={message} session={session} />;
  } else if (message.role === 'model' && message.type === 'compaction') {
    return <CompactionMessage status={message.status} summary={message.summary} />;
  } else if (message.role === 'model' && message.type === 'error') {
    return (
      <AnimateMessage>
        <ErrorMessage error={message.error} errorDetails={message.error_details} />
      </AnimateMessage>
    );
  } else {
    // typescript hack to ensure all cases are handled
    const never: never = message;
    // oxlint-disable-next-line no-console
    console.error('Unknown message type', never);
    return null;
  }
}

function areMessagePropsEqual(previous: MessageProps, next: MessageProps) {
  // statefulMessages creates fresh objects for unchanged rows, so reference
  // equality alone would still rerender long chat history on each backend update.
  return (
    previous.session === next.session &&
    (previous.isLastMessage ?? false) === (next.isLastMessage ?? false) &&
    (previous.isStreaming ?? false) === (next.isStreaming ?? false) &&
    (previous.isRecovering ?? false) === (next.isRecovering ?? false) &&
    (previous.isStopped ?? false) === (next.isStopped ?? false) &&
    previous.skillSlugs === next.skillSlugs &&
    areStatefulMessagesEqual(previous.message, next.message)
  );
}

function areStatefulMessagesEqual(previous: CopilotStatefulMessage, next: CopilotStatefulMessage) {
  if (previous === next) {
    return true;
  }

  if (previous.role !== next.role || previous.type !== next.type || previous.timestamp !== next.timestamp) {
    return false;
  }

  if (previous.role === 'user') {
    if (next.role !== 'user') {
      return false;
    }

    return previous.text === next.text && areAttachmentsEqual(previous.attachments, next.attachments);
  }

  if (next.role !== 'model') {
    return false;
  }

  switch (previous.type) {
    case 'text':
      if (next.type !== 'text') {
        return false;
      }

      return (
        previous.text === next.text &&
        previous.thought === next.thought &&
        previous.duration === next.duration &&
        getMetaSignature(previous.meta) === getMetaSignature(next.meta)
      );
    case 'tool_call':
      if (next.type !== 'tool_call') {
        return false;
      }

      return getToolCallsSignature(previous.tool_calls) === getToolCallsSignature(next.tool_calls);
    case 'compaction':
      if (next.type !== 'compaction') {
        return false;
      }

      return previous.status === next.status && previous.summary === next.summary;
    case 'error':
      if (next.type !== 'error') {
        return false;
      }

      return previous.error === next.error && previous.error_details === next.error_details;
    default:
      return false;
  }
}

function areAttachmentsEqual(
  previous: Extract<CopilotStatefulMessage, { role: 'user' }>['attachments'],
  next: Extract<CopilotStatefulMessage, { role: 'user' }>['attachments'],
) {
  if (previous.length !== next.length) {
    return false;
  }

  return previous.every((attachment, index) => {
    const nextAttachment = next[index];
    return (
      attachment.content === nextAttachment?.content &&
      attachment.mime_type === nextAttachment.mime_type &&
      attachment.display_name === nextAttachment.display_name
    );
  });
}

function getMetaSignature(meta: Extract<CopilotStatefulMessage, { role: 'model'; type: 'text' }>['meta']) {
  return JSON.stringify(meta);
}

function getToolCallsSignature(
  toolCalls: Extract<CopilotStatefulMessage, { role: 'model'; type: 'tool_call' }>['tool_calls'],
) {
  return JSON.stringify(
    toolCalls.map((toolCall) => {
      // respond is a callback recreated during stateful-message derivation; the
      // UI only cares whether a response affordance exists, not the function identity.
      const { respond: _respond, ...serializableToolCall } = toolCall;
      return {
        ...serializableToolCall,
        hasRespond: _respond != null,
      };
    }),
  );
}

export const Message = memo(MessageComponent, areMessagePropsEqual);
Message.displayName = 'Message';
